import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check } from 'lucide-react'
import { PHOTOGRAPHY_PROJECTS, PHOTOGRAPHY_SERVICE_AREA, CREATIVE_LEAD } from '@gjl/voice/pricing'

// Prices render from @gjl/voice/pricing. Edit them there.

const PROBLEMS = [
  {
    problem: 'Your photos undersell the performance',
    description:
      'A phone shot from the back row is what ends up on the website, the season brochure, and the grant application. The audience that night heard something better than that photo shows.',
  },
  {
    problem: 'Press and grants keep asking for images you do not have',
    description:
      'High-resolution, publishable photos are the first thing a newspaper, a presenter, or a funder requests, and the folder is empty or five years old.',
  },
  {
    problem: 'Your people pages are a patchwork',
    description:
      'One director has a studio headshot, another a cropped wedding photo, a third a selfie. Shot together, your roster reads like one organization.',
  },
  {
    problem: 'The season launches with last season’s images',
    description:
      'New programming, new venue, new faces, and the announcement still runs on photos from two years ago because nobody was hired to take new ones.',
  },
]

// Real work: the 2026 Chroma International Music Festival (Miskolc), shot by
// Danielle Dinstman. Reuses the optimized assets committed for the /portfolio
// Photography gallery item (public/portfolio/photography) rather than a second
// copy. A curated one-per-program set; the full gallery lives on /portfolio.
const CHROMA_WORK = [
  {
    src: '/portfolio/photography/DSCF2911-thumb.webp',
    caption: 'Faculty',
    alt: 'The Chroma Festival faculty in the organ hall, Miskolc',
  },
  {
    src: '/portfolio/photography/DSCF2907-thumb.webp',
    caption: 'Piano, young artists',
    alt: 'The young-artist piano cohort at the Chroma Festival, Miskolc',
  },
  {
    src: '/portfolio/photography/DSCF2898-thumb.webp',
    caption: 'Piano, adult program',
    alt: 'The adult piano program at the Chroma Festival, Miskolc',
  },
  {
    src: '/portfolio/photography/DSCF2929-thumb.webp',
    caption: 'Composition',
    alt: 'The composition program at the Chroma Festival, Miskolc',
  },
]

const FAQS = [
  {
    q: 'Where do you shoot?',
    a: PHOTOGRAPHY_SERVICE_AREA,
  },
  {
    q: 'What do we get after a session?',
    a: 'An edited, web-ready gallery plus full-resolution files. Delivered sized for your website, socials, and print, so nobody on your team has to resize anything.',
  },
  {
    q: 'Can you shoot a live concert without disrupting it?',
    a: 'Yes. Performance coverage is planned around the program: where to stand, when to move, what stays silent. You get the concert documented and your audience never notices the camera.',
  },
  {
    q: 'Who owns the photos?',
    a: 'You do, with full usage rights for your marketing, press, and grant materials. Same policy as everything else here: you can leave with everything.',
  },
]

export default function PhotographyPage() {
  const breadcrumbs = generateBreadcrumbs('/services/photography', breadcrumbLabels)

  useSEO({
    title: 'Photography for Music Organizations',
    description:
      'Concert photography, headshots, and season photography days for music organizations in the Los Angeles area. Edited, web-ready galleries from $300 per session.',
    url: '/services/photography',
    canonical: '/services/photography',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Photography for Music Organizations',
      description:
        'Concert and rehearsal photography, roster headshots, and season photography days for festivals, churches, choruses, and music schools.',
      url: '/services/photography',
    }),
  })

  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const problemsRef = useStaggeredGrid<HTMLDivElement>('.problem-card', { stagger: 0.08 })
  const projectsRef = useStaggeredGrid<HTMLDivElement>('.project-card', { stagger: 0.08 })
  const galleryRef = useStaggeredGrid<HTMLDivElement>('.work-tile', { stagger: 0.08 })
  const faqRef = useScrollReveal<HTMLDivElement>('.faq-item', { stagger: 0.08 })

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Photos that match the performance</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          The concert lasts one night. The photos are what your website, your press kit, and your next grant application
          run on for years. Shot by a working artist who knows where to stand in a hall.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          Performance coverage, headshots, and season photography days, delivered as edited, web-ready galleries you own
          outright.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </motion.div>
          </a>
          <Link to="/services/design">
            <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
              Graphic Design
            </Button>
          </Link>
        </div>
      </section>

      {/* Problems */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Why organizations book a photographer</h2>
        <div ref={problemsRef} className="grid gap-6 md:grid-cols-2">
          {PROBLEMS.map((item) => (
            <motion.div
              key={item.problem}
              className="problem-card border-border space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.problem}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Who does the work */}
      <section className="border-primary/30 bg-primary/5 mx-auto max-w-4xl space-y-4 rounded-2xl border p-8 text-center">
        <h2 className="text-fluid-h2">An artist behind the camera</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl">{CREATIVE_LEAD.credit}</p>
        <p className="text-muted-foreground mx-auto max-w-2xl text-sm">
          Someone who has hung a photography exhibition reads a concert differently than a generalist with a camera:
          light, gesture, and the moment a phrase lands.
        </p>
      </section>

      {/* Recent work — the Chroma Festival */}
      <section className="space-y-8">
        <div className="mx-auto max-w-3xl space-y-3 text-center">
          <h2 className="text-fluid-h2">Recent work: the Chroma Festival</h2>
          <p className="text-muted-foreground">
            The 2026 Chroma International Music Festival in Miskolc, Hungary: the full company, the faculty, and each
            program, from the young artists to the composers.
          </p>
        </div>
        <figure className="border-border overflow-hidden rounded-2xl border">
          <img
            src="/portfolio/photography/DSCF2893.webp"
            alt="The full company of the 2026 Chroma International Music Festival gathered in the organ hall, Miskolc"
            width={1600}
            height={1066}
            loading="lazy"
            className="aspect-[3/2] w-full object-cover"
          />
          <figcaption className="text-muted-foreground px-4 py-3 text-sm">The full company, Miskolc</figcaption>
        </figure>
        <div ref={galleryRef} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {CHROMA_WORK.map((item) => (
            <figure key={item.src} className="work-tile border-border overflow-hidden rounded-xl border">
              <img
                src={item.src}
                alt={item.alt}
                width={760}
                height={507}
                loading="lazy"
                className="aspect-[3/2] w-full object-cover"
              />
              <figcaption className="text-muted-foreground px-3 py-2 text-sm">{item.caption}</figcaption>
            </figure>
          ))}
        </div>
        <p className="text-muted-foreground text-center text-sm">
          Photographs by Danielle Dinstman, on location at the Chroma International Music Festival.{' '}
          <Link to="/portfolio/chroma-festival-photography" className="text-primary underline">
            See the full set
          </Link>
          .
        </p>
      </section>

      {/* Sessions */}
      <section className="space-y-8">
        <h2 className="text-fluid-h2 text-center">The sessions</h2>
        <div ref={projectsRef} className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {PHOTOGRAPHY_PROJECTS.map((project) => (
            <motion.div
              key={project.id}
              className="project-card border-border flex flex-col space-y-3 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{project.name}</h3>
              <div className="text-primary text-lg font-bold">{project.price}</div>
              <p className="text-muted-foreground flex-1 text-sm">{project.note}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-muted-foreground mx-auto max-w-2xl text-center text-sm">
          {PHOTOGRAPHY_SERVICE_AREA} Need the graphics side too? See{' '}
          <Link to="/services/design" className="text-primary underline">
            Graphic Design
          </Link>
          .
        </p>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Questions</h2>
        <div ref={faqRef} className="space-y-6">
          {FAQS.map((f) => (
            <div key={f.q} className="faq-item border-border rounded-lg border p-6">
              <h3 className="text-foreground mb-2 font-semibold">{f.q}</h3>
              <p className="text-muted-foreground text-sm">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-col items-center gap-3 pt-4 text-center">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="cursor-pointer">
              Book a Free Call →
            </Button>
          </a>
          <div className="text-muted-foreground flex items-center justify-center gap-2 text-sm">
            <Check className="text-primary h-4 w-4" strokeWidth={2.5} />
            <span>Full-resolution files and full usage rights, yours after every session.</span>
          </div>
        </div>
      </section>
    </div>
  )
}
